import CloseIcon from '@mui/icons-material/Close';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {DATE_FORMAT, getPATDay, humanDateStamp} from '@account/utils/dates';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {JIRA_KEY_RE, formatErrorMsg, getError} from '@account/utils/errors';
import {extendTermDates} from '@account/api';
import {useMutation, useQueryClient} from '@tanstack/react-query';

const ExtendTermDatesModal = ({
  open,
  onClose,
  productAccessId,
  term,
  onHighlightTerm,
}) => {
  const queryClient = useQueryClient();
  const minDate = getPATDay(term.end_date).plus({days: 1});
  // Prepopulate with the current end date as the anchor the user extends from.
  // It sits one day below minDate, so the picker flags it and canSubmit stays
  // false until the user actually moves the date forward (guards a no-op submit).
  const [newEndDate, setNewEndDate] = useState(() => getPATDay(term.end_date));
  const [supportTicketRef, setSupportTicketRef] = useState('');
  const [notes, setNotes] = useState('');
  const [blockingError, setBlockingError] = useState(null);
  const [generalError, setGeneralError] = useState(null);

  const canSubmit =
    newEndDate !== null &&
    +newEndDate >= +minDate &&
    JIRA_KEY_RE.test(supportTicketRef.trim()) &&
    !!notes.trim();

  const {mutate, isLoading} = useMutation(
    () =>
      extendTermDates(productAccessId, term.id, {
        new_end_date: newEndDate.toISODate(),
        support_ticket_ref: supportTicketRef.trim(),
        notes: notes.trim(),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccessTerms', productAccessId]);
        queryClient.invalidateQueries(['productAccess', productAccessId]);
        onClose();
      },
      onError: err => {
        const error = getError(err);
        if (error.statusCode === 422 && error.blocking_pat_id) {
          setBlockingError(error);
        } else {
          setGeneralError(formatErrorMsg(error));
        }
      },
    }
  );

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleHighlightBlocking = () => {
    if (blockingError?.blocking_pat_id) {
      onHighlightTerm(blockingError.blocking_pat_id);
      handleClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Extend access period
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{position: 'absolute', right: 8, top: 8}}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{mt: 1}}>
            <Typography variant="body2" color="text.secondary">
              Current end date: <strong>{humanDateStamp(term.end_date)}</strong>
            </Typography>

            <DatePicker
              label="New end date"
              format={DATE_FORMAT}
              timezone="UTC"
              value={newEndDate}
              minDate={minDate}
              onChange={date => {
                setNewEndDate(date);
                setBlockingError(null);
                setGeneralError(null);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  inputProps: {placeholder: 'YYYY-MM-DD'},
                },
              }}
            />

            {blockingError && (
              <Alert severity="error" variant="outlined">
                The next access period (starting{' '}
                {humanDateStamp(blockingError.blocking_pat_start_date)})
                overlaps with your new end date. Delete that period first, then
                retry.{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleHighlightBlocking}
                >
                  Show conflicting period
                </Link>
              </Alert>
            )}

            {generalError && (
              <Typography color="error" variant="body2">
                {generalError}
              </Typography>
            )}

            <TextField
              label="Support ticket ref"
              value={supportTicketRef}
              onChange={e => setSupportTicketRef(e.target.value)}
              placeholder="CGO-1234"
              error={
                !!supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim())
              }
              helperText={
                supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim())
                  ? 'Must match format PROJECT-1234 (e.g. CGO-1234)'
                  : 'Format: PROJECT-1234 (e.g. CGO-1234)'
              }
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => mutate()}
            variant="contained"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Saving…' : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      <Notifier open={isLoading} message="Extending access period…" progress />
    </>
  );
};

ExtendTermDatesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onHighlightTerm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  productAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  term: PropTypes.shape({
    end_date: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};

export default ExtendTermDatesModal;
